import React from "react";

import { useMutation } from "@apollo/client";
import { Grid, Box, Typography, Theme } from "@mui/material";
import FormGroup from "@mui/material/FormGroup";
import Switch from "@mui/material/Switch";
import { useParams } from "react-router-dom";
import { withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { ClientStatus } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError, showInvalidError } from "@/utils/errors";

const AntSwitch = withStyles(Switch, (theme: Theme, _param, classes) => ({
  root: {
    width: 34,
    height: 21,
    padding: 0,
    display: "flex",
  },
  switchBase: {
    padding: 4,
    color: theme.palette.grey[500],
    [`&.${classes.checked}`]: {
      transform: "translateX(14px)",
      color: theme.palette.common.white,
      [`& + .${classes.track}`]: {
        opacity: 1,
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  thumb: {
    width: 12,
    height: 12,
    boxShadow: "none",
  },
  track: {
    border: `1px solid ${theme.palette.grey[500]}`,
    borderRadius: 24 / 2,
    opacity: 1,
    backgroundColor: theme.palette.common.white,
  },
  checked: {},
}));

type PublishPlanProps = {
  isPublished: boolean;
  planId: string;
};

const publishProgramMutation = graphql(`
  mutation PublishProgram($id: ID!, $published: Boolean!) {
    publishProgram(input: { id: $id, published: $published }) {
      program {
        id
        published
        client {
          id
          status
        }
      }
    }
  }
`);

const PublishPlanSwitch: React.FC<PublishPlanProps> = ({
  planId,
  isPublished,
}) => {
  const snackbar = useGlobalSnackbar();
  const [publishProgram] = useMutation(publishProgramMutation);
  const { clientId } = useParams<{ clientId: string }>();
  const handleChange = async () => {
    try {
      const result = await publishProgram({
        variables: { id: planId, published: !isPublished },
        optimisticResponse: {
          __typename: "Mutation",
          publishProgram: {
            __typename: "PublishProgramPayload",
            program: {
              __typename: "Program",
              id: planId,
              published: !isPublished,
              client: {
                __typename: "Client",
                id: clientId ?? "",
                status: ClientStatus.Active,
              },
            },
          },
        },
      });
      if (result.data?.publishProgram?.program?.published) {
        snackbar.open("Program is published successfully");
      } else {
        snackbar.open("Program is set to draft");
      }
    } catch (e) {
      snackbar.open(
        showInvalidError(
          e,
          "This program cannot be published because it overlaps with other published programs"
        )
      );
    }
  };
  return (
    <Box
      marginLeft={3}
      marginRight={3}
      display="flex"
      flexDirection="row"
      justifyContent="center"
    >
      <FormGroup row>
        <Typography component="div">
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>Draft</Grid>
            <Grid item>
              <AntSwitch
                checked={isPublished}
                onChange={handleChange}
                name="checkedC"
              />
            </Grid>
            <Grid item>
              <Typography
                style={{ color: isPublished ? colors.primary : colors.grey }}
              >
                Publish
              </Typography>
            </Grid>
          </Grid>
        </Typography>
      </FormGroup>
    </Box>
  );
};

export default PublishPlanSwitch;
